import React, { forwardRef } from 'react';
import { Button } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';

const AET_BUTTON_CARD_CLASS = 'aet-button-card';

const CardRoot = styled('div')({
  '&&': {
    padding: 16,
    borderRadius: 8,
    boxSizing: 'border-box',
    backgroundColor: mixins.chalkWhiteFont,

    '& *': {
      boxSizing: 'border-box'
    }
  }
});

const buttonCardRootStyles = {
  padding: '16px !important',
  borderRadius: 8,
  boxSizing: 'border-box',
  display: 'flex !important',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  textAlign: 'left',
  textTransform: 'none',
  width: '100%',
  minWidth: 'unset',
  maxWidth: 'none',
  height: 'auto',
  color: 'inherit',
  lineHeight: 'inherit',
  fontWeight: 'inherit',
  fontFamily: 'inherit',
  border: 'none',
  boxShadow: 'none',
  gap: 16,

  '& *': {
    boxSizing: 'border-box'
  },

  '&&&': {
    backgroundColor: 'var(--card-bg) !important',
    background: 'var(--card-bg) !important'
  },

  '&:hover': {
    backgroundColor: 'var(--card-bg) !important',
    background: 'var(--card-bg) !important'
  }
};

const ButtonCard = styled(Button)({
  '&&&': buttonCardRootStyles
});

/**
 * To update styles, use the classes prop
 *
 * @example
 * <CustomCard
 *   classes={{
 *     root: classes.yourClassName
 *   }}
 * >
 *   {children}
 * </CustomCard>
 *
 * @param {Object} props
 * @returns {React.Component} - A custom card component
 */

const CustomCard = forwardRef((props, ref) => {
  const {
    backgroundColor,
    children,
    style,
    className,
    classes: classesProp,
    isButtonCard = false,
    onClick
  } = props;
  const resolvedClassName = className || classesProp?.root || '';

  if (isButtonCard) {
    return (
      <ButtonCard
        className={[AET_BUTTON_CARD_CLASS, resolvedClassName].filter(Boolean).join(' ')}
        style={{
          backgroundColor,
          ...style
        }}
        onClick={onClick}
        disableRipple
        disableElevation
      >
        {children}
      </ButtonCard>
    );
  }

  return (
    <CardRoot
      ref={ref}
      className={resolvedClassName}
      style={{
        backgroundColor,
        ...style
      }}
    >
      {children}
    </CardRoot>
  );
});

CustomCard.propTypes = {
  backgroundColor: PropTypes.string,
  children: PropTypes.node,
  style: PropTypes.object,
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string
  }),
  isButtonCard: PropTypes.bool,
  onClick: PropTypes.func
};

CustomCard.defaultProps = {
  backgroundColor: '',
  children: null,
  style: {},
  className: '',
  classes: {},
  isButtonCard: false,
  onClick: () => {}
};

export default CustomCard;
